import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  DEFAULT_APP_BACKGROUND_CUSTOM,
  DEFAULT_APP_BACKGROUND_THEME,
  normalizeAppBackgroundCustom,
  normalizeAppBackgroundTheme,
} from "@/lib/appBackgroundThemes";

export const UI_PREFERENCES_UPDATED_EVENT = "visor:ui-preferences-updated";

/** Por defecto el asistente Clientes está en construcción para el comprador. */
export const DEFAULT_BUYER_ASISTENTE_CLIENTES_UNDER_CONSTRUCTION = true;

/**
 * Preferencias globales de interfaz (system_config.ui_preferences).
 */
export function useUiPreferences() {
  const [buyerModuleSidebarEnabled, setBuyerModuleSidebarEnabled] = useState(false);
  const [appBackgroundTheme, setAppBackgroundTheme] = useState(DEFAULT_APP_BACKGROUND_THEME);
  const [appBackgroundCustom, setAppBackgroundCustom] = useState(DEFAULT_APP_BACKGROUND_CUSTOM);
  const [buyerAsistenteClientesUnderConstruction, setBuyerAsistenteClientesUnderConstruction] = useState(
    DEFAULT_BUYER_ASISTENTE_CLIENTES_UNDER_CONSTRUCTION,
  );
  const [loading, setLoading] = useState(true);

  const applyPrefs = useCallback((data) => {
    if (!data || typeof data !== "object") return;
    if ("buyer_module_sidebar_enabled" in data) {
      setBuyerModuleSidebarEnabled(Boolean(data.buyer_module_sidebar_enabled));
    }
    if ("app_background_theme" in data) {
      setAppBackgroundTheme(normalizeAppBackgroundTheme(data.app_background_theme));
    }
    if ("app_background_custom" in data) {
      setAppBackgroundCustom(normalizeAppBackgroundCustom(data.app_background_custom));
    }
    if ("buyer_asistente_clientes_under_construction" in data) {
      setBuyerAsistenteClientesUnderConstruction(
        Boolean(data.buyer_asistente_clientes_under_construction),
      );
    }
  }, []);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/system/ui-preferences");
      applyPrefs(data);
    } catch {
      try {
        const { data: info } = await api.get("/system/info", { timeout: 60000 });
        applyPrefs(info?.ui_preferences || {});
      } catch {
        setBuyerModuleSidebarEnabled(false);
        setAppBackgroundTheme(DEFAULT_APP_BACKGROUND_THEME);
        setAppBackgroundCustom(DEFAULT_APP_BACKGROUND_CUSTOM);
        setBuyerAsistenteClientesUnderConstruction(DEFAULT_BUYER_ASISTENTE_CLIENTES_UNDER_CONSTRUCTION);
      }
    } finally {
      setLoading(false);
    }
  }, [applyPrefs]);

  useEffect(() => {
    reload();
    const onUpdate = (event) => {
      if (event?.detail && typeof event.detail === "object") {
        applyPrefs(event.detail);
        setLoading(false);
      }
      reload();
    };
    window.addEventListener(UI_PREFERENCES_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(UI_PREFERENCES_UPDATED_EVENT, onUpdate);
  }, [reload, applyPrefs]);

  return {
    buyerModuleSidebarEnabled,
    appBackgroundTheme,
    appBackgroundCustom,
    buyerAsistenteClientesUnderConstruction,
    loading,
    reload,
  };
}

function prefsDetailFromResponse(data, fallback = {}) {
  return {
    buyer_module_sidebar_enabled: data?.buyer_module_sidebar_enabled ?? fallback.buyer_module_sidebar_enabled,
    app_background_theme: data?.app_background_theme ?? fallback.app_background_theme,
    app_background_custom: data?.app_background_custom ?? fallback.app_background_custom,
    buyer_asistente_clientes_under_construction:
      data?.buyer_asistente_clientes_under_construction
      ?? fallback.buyer_asistente_clientes_under_construction,
  };
}

export async function updateBuyerModuleSidebarPreference(enabled) {
  const { data } = await api.put("/system/ui-preferences", {
    buyer_module_sidebar_enabled: Boolean(enabled),
  });
  window.dispatchEvent(
    new CustomEvent(UI_PREFERENCES_UPDATED_EVENT, {
      detail: prefsDetailFromResponse(data, {
        buyer_module_sidebar_enabled: Boolean(enabled),
      }),
    }),
  );
  return data;
}

/**
 * @param {string} theme
 * @param {object|null} [custom] — paleta/textura/intensidad (se envía si theme=custom o si se pasa)
 */
export async function updateAppBackgroundThemePreference(theme, custom = null) {
  const normalized = normalizeAppBackgroundTheme(theme);
  const payload = { app_background_theme: normalized };
  if (custom != null || normalized === "custom") {
    payload.app_background_custom = normalizeAppBackgroundCustom(custom);
  }
  const { data } = await api.put("/system/ui-preferences", payload);
  window.dispatchEvent(
    new CustomEvent(UI_PREFERENCES_UPDATED_EVENT, {
      detail: prefsDetailFromResponse(data, {
        app_background_theme: normalized,
        app_background_custom: payload.app_background_custom,
      }),
    }),
  );
  return data;
}

/** Diseñador: mostrar u ocultar WhatsApp/borradores del asistente Clientes para el comprador. */
export async function updateBuyerAsistenteClientesUnderConstruction(enabled) {
  const { data } = await api.put("/system/ui-preferences", {
    buyer_asistente_clientes_under_construction: Boolean(enabled),
  });
  window.dispatchEvent(
    new CustomEvent(UI_PREFERENCES_UPDATED_EVENT, {
      detail: prefsDetailFromResponse(data, {
        buyer_asistente_clientes_under_construction: Boolean(enabled),
      }),
    }),
  );
  return data;
}
